import React, { useMemo, useEffect, useState, useRef } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import get from 'lodash.get';
import InputMask from 'react-input-mask';
import styles from './text-input.module.scss';
import {
  applyClasses,
  applyProps,
  useKeyPress,
  constants,
  isTablet,
} from '../shared/js';
import { Dropdown, Button, Icons } from '..';

const { ESCAPE_KEY, ENTER_KEY } = constants;

const doNothing = () => {};

const TextInput = ({
  id,
  type,
  name,
  defaultValue = '',
  placeholder,
  searchable,
  searchResults,
  emptySearchResultsMessage,
  minLengthForSearchResults,
  onChange,
  onSelect,
  onFocus,
  hidePlaceHolderOnFocus,
  prefix,
  suffix,
  contentSuffix,
  number,
  validation,
  label,
  mask,
  disabled,
  disableAutocomplete,
  highlight,
  maxLength,
  dirtyErrors,
  submitErrors,
  dirtyTouchedErrors,
  ...props
}) => {
  const { formatMessage } = useIntl();
  const wrapperRef = useRef();
  const formMethods = useFormContext();
  const escPressed = useKeyPress(ESCAPE_KEY);
  const inputDefault = get(formMethods?.getValues(), name) || defaultValue;
  const [value, setValue] = useState(inputDefault ? inputDefault : '');
  const [lastTextOnSelect, setLastTextOnSelect] = useState();
  const [showResults, setShowResults] = useState(false);
  const [focused, setFocused] = useState(false);
  const register = formMethods?.register ? formMethods?.register : () => {};
  const formProps = { ...register(name, { ...validation }) };
  const setFormValue = formMethods?.setValue ? formMethods?.setValue : () => {};
  const errors = formMethods?.formState.errors;
  const error = errors && get(errors, name);
  const errorType = error?.type;
  const noResultID = `${id}_noResult`;
  const [showPlaceHolder, setShowPlaceHolder] = useState(true);
  const isDirty = !!get(formMethods?.formState.dirtyFields, name);
  const isTouched = !!get(formMethods?.formState.touchedFields, name);
  const isSubmitted = formMethods?.formState.isSubmitted;
  const shouldShowError = useMemo(() => {
    if (!errorType) return false;
    if (!isDirty && dirtyErrors.includes(errorType)) {
      return false;
    }
    if (!isDirty && !isTouched && dirtyTouchedErrors.includes(errorType)) {
      return false;
    }
    if (!isSubmitted && submitErrors.includes(errorType)) {
      return false;
    }
    return true;
  }, [
    isDirty,
    isTouched,
    isSubmitted,
    dirtyErrors,
    dirtyTouchedErrors,
    submitErrors,
    errorType,
  ]);

  const refreshShowResultsState = () => {
    if (!searchable || value === lastTextOnSelect) {
      return;
    }
    const hasAchievedMinimumLength =
      minLengthForSearchResults === undefined ||
      value.length >= minLengthForSearchResults;
    if (showResults) {
      if (!hasAchievedMinimumLength) {
        setShowResults(false);
      }
      return;
    }
    if (!hasAchievedMinimumLength) {
      return;
    }
    setShowResults(searchResults?.length > 0 || !!emptySearchResultsMessage);
  };

  useEffect(() => {
    if (searchable) {
      refreshShowResultsState();
    }
  }, [searchResults, value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!escPressed) return;
    setShowResults(false);
    setFocused(false);
  }, [escPressed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!searchable) return;
    if (focused) document.body.classList.add(styles.scrollLockMobile);
    else document.body.classList.remove(styles.scrollLockMobile);
  }, [searchable, focused]);

  useEffect(() => {
    setValue(inputDefault);
  }, [inputDefault]); // eslint-disable-line react-hooks/exhaustive-deps

  const formattedResults = useMemo(() => {
    if (!highlight) return searchResults;
    return highlightedSearchResults(searchResults, value);
  }, [value, highlight, searchResults]);

  const handleChange = (e) => {
    e.preventDefault();
    if (number) {
      const charEntered = e.target.value.slice(-1);
      if (isNaN(charEntered)) return;
    }

    setValue(e.target.value);
    onChange && onChange(e.target.value);
    formProps.onChange && formProps.onChange(e);
  };

  const handleKeyDown = (e) => {
    if (e.keyCode !== ENTER_KEY) return;
    if (searchable && (searchResults.length > 0 || emptySearchResultsMessage)) {
      e.preventDefault();
    }
  };

  const selectResult = (result) => {
    const labelOrValue = result.selectedLabel || result.value;
    setValue(labelOrValue);
    setLastTextOnSelect(labelOrValue);
    setShowResults(false);
    setFocused(false);
    onSelect && onSelect(result.value, name);
    setFormValue(name, labelOrValue);
  };

  const shouldCloseDropdown = (e) => {
    if (isTablet()) {
      return e.relatedTarget && !wrapperRef.current.contains(e.relatedTarget);
    }
    return !wrapperRef.current.contains(e.relatedTarget);
  };

  const handleDivBlur = (e) => {
    if (hidePlaceHolderOnFocus) setShowPlaceHolder(true);
    if (shouldCloseDropdown(e)) {
      setShowResults(false);
      setFocused(false);
    }
  };

  const handleFocus = () => {
    if (hidePlaceHolderOnFocus) setShowPlaceHolder(false);

    setFocused(true);
    if (searchable) {
      refreshShowResultsState();
    }
    onFocus?.();
  };

  const classes = applyClasses(props, styles);

  const inputProps = {
    ...formProps,
    id: id || `input_${name}`,
    type: type,
    name: name,
    value: value,
    placeholder: showPlaceHolder ? placeholder : undefined,
    ...applyProps(props),
    onChange: handleChange,
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
    className: cn(
      styles.textInput,
      props.className,
      {
        [styles.hasPrefix]: prefix,
        [styles.hasSuffix]: suffix,
      },
      classes
    ),
  };

  return (
    <>
      {label && (
        <label className={styles.label} htmlFor={`input_${name}`}>
          {label}
        </label>
      )}
      <div
        id="inputWrapper"
        onBlur={handleDivBlur}
        ref={wrapperRef}
        className={cn(
          styles.wrapper,
          { [styles.searchable]: searchable, [styles.focused]: focused },
          classes
        )}
      >
        <div className={styles.mobileInputRow}>
          {searchable && (
            <div
              className={cn(styles.mobileBackButton, styles.mobileRowButton)}
            >
              <Button p0 onClick={() => setFocused(false)}>
                <Icons.Chevron />
              </Button>
            </div>
          )}
          <div className={styles.prefix}>{prefix}</div>
          {mask ? (
            <InputMask
              id={id}
              {...inputProps}
              mask={mask}
              maskChar=""
              formatChars={{
                9: '[0-9]',
                a: '[A-Za-z]',
                '*': '[A-Za-z0-9]',
                '~': '[A-Za-z0-9 ]',
              }}
              disabled={disabled}
              autoComplete={
                disableAutocomplete || searchable ? 'off' : undefined
              }
              maxLength={maxLength}
            />
          ) : (
            <input
              id={id}
              {...inputProps}
              disabled={disabled}
              autoComplete={
                disableAutocomplete || searchable ? 'off' : undefined
              }
              maxLength={maxLength}
            />
          )}
          {searchable && (
            <div
              className={cn(styles.mobileSubmitButton, styles.mobileRowButton)}
            >
              <Button
                p0
                underline
                tertiary
                link
                onClick={() => setFocused(false)}
              >
                {formatMessage({
                  id: 'po.modal.submit_button',
                  defaultMessage: 'Submit',
                })}
              </Button>
            </div>
          )}
        </div>
        {searchable && focused && (
          <div className={cn(styles.results, classes)}>
            {searchResults.length ? (
              <Dropdown.Options
                options={formattedResults}
                onClick={selectResult}
                isOpen={showResults}
                id={id}
                searchResults
              />
            ) : emptySearchResultsMessage ? (
              <Dropdown.Options
                options={[
                  {
                    value: '',
                    label: <span>{emptySearchResultsMessage}</span>,
                  },
                ]}
                onClick={doNothing}
                isOpen={showResults}
                id={noResultID}
              />
            ) : null}
          </div>
        )}
        <div className={styles.suffix}>{suffix}</div>
        {contentSuffix && (
          <div className={styles.contentSuffixContainer} tabIndex={-1}>
            <span className={styles.contentSuffixSpacer}>{value}</span>
            <span className={styles.contentSuffix}>{contentSuffix}</span>
          </div>
        )}
      </div>
      {error && shouldShowError && (
        <span role="alert">
          {error.message || (
            <FormattedMessage
              id="forms.error"
              description="Error message"
              defaultMessage="Field error"
            />
          )}
        </span>
      )}
    </>
  );
};

// Modify the search result children to highlight the searched string
const highlightedSearchResults = (searchResults, value) => {
  return searchResults?.map((searchResult, index) => {
    const label = searchResult.label.props.children;
    const searchLabel = searchResult.label.props.children.toLowerCase();
    const searchString = value.toLowerCase();
    const start = searchLabel.indexOf(searchString);

    const end = start + searchString.length;
    const newLabel = (
      <span key={index} className={styles.highlightedResult}>
        {start === -1 ? (
          label
        ) : (
          <>
            {label.substring(0, start)}
            <span className={styles.highlight}>
              {label.substring(start, end)}
            </span>
            {label.substring(end)}
          </>
        )}
      </span>
    );
    return {
      ...searchResult,
      label: newLabel,
    };
  });
};

TextInput.propTypes = {
  /** The ID of the input. Useful to select the input during testing. */
  id: PropTypes.string,
  /** The type attribute of the input */
  type: PropTypes.string,
  /** The name of the input */
  name: PropTypes.string,
  /** The default value of the input */
  defaultValue: PropTypes.string,
  /** The placeholder value of the input */
  placeholder: PropTypes.string,
  /** A function to run when the input's value changes */
  onChange: PropTypes.func,
  /** Applies primary styles to the input */
  primary: PropTypes.bool,
  /** This input can be used to show a list of results based on its content */
  searchable: PropTypes.bool,
  /** Results filtered in a searchable input */
  searchResults: PropTypes.arrayOf(PropTypes.object),
  /** Message to show when there's no search results */
  emptySearchResultsMessage: PropTypes.string,
  /** Minimum length of value before showing search results */
  minLengthForSearchResults: PropTypes.number,
  /** Applies secondary styles to the input */
  secondary: PropTypes.bool,
  /** Makes the input fill the width of its container */
  fullWidth: PropTypes.bool,
  /** Styles applied to the input element */
  style: PropTypes.object,
  /** Class name applied to the input element */
  className: PropTypes.string,
  /** Styles applied to the dropdown results wrapper element */
  resultsStyle: PropTypes.object,
  /** Class name applied to the dropdown results wrapper element */
  resultsClassName: PropTypes.string,
  /** Styles applied to the individual result button elements */
  resultStyle: PropTypes.object,
  /** Class name applied to the individual result button elements */
  resultClassName: PropTypes.string,
  /** Component or element to be displayed on the left side of the input */
  prefix: PropTypes.any,
  /** Component or element to be displayed on the right side of the input */
  suffix: PropTypes.any,
  /** Component or element to be displayed following the input content */
  contentSuffix: PropTypes.any,
  /** Restrict this input to only allow numbers */
  number: PropTypes.bool,
  /** The validation rules for the input, see https://react-hook-form.com/get-started#Applyvalidation */
  validation: PropTypes.object,
  /** A callback function that runs when a searchable field's option is selected */
  onSelect: PropTypes.func,
  /** A callback function that runs when an input field is focused */
  onFocus: PropTypes.func,
  /** Hide the placeholder text when the input field is focused */
  hidePlaceHolderOnFocus: PropTypes.bool,
  /** An optional small label above the input */
  label: PropTypes.any,
  /** A mask to add to the input,  see https://www.npmjs.com/package/react-input-mask for formats */
  mask: PropTypes.string,
  /** Disable the input */
  disabled: PropTypes.bool,
  /** Sets autocomplete="off" on the component */
  disableAutocomplete: PropTypes.bool,
  /** Highlight the search string in the results */
  highlight: PropTypes.bool,
  /** The maximum number of characters allowed in the input */
  maxLength: PropTypes.number,
  dirtyErrors: PropTypes.arrayOf(
    PropTypes.oneOf([
      'required',
      'minLength',
      'maxLength',
      'pattern',
      'validate',
    ])
  ),
  dirtyTouchedErrors: PropTypes.arrayOf(
    PropTypes.oneOf([
      'required',
      'minLength',
      'maxLength',
      'pattern',
      'validate',
    ])
  ),
  submitErrors: PropTypes.arrayOf(
    PropTypes.oneOf([
      'required',
      'minLength',
      'maxLength',
      'pattern',
      'validate',
    ])
  ),
};

TextInput.defaultProps = {
  type: 'text',
  defaultValue: '',
  fullWidth: false,
  name: undefined,
  onChange: undefined,
  placeholder: undefined,
  primary: false,
  searchable: false,
  searchResults: [],
  emptySearchResultsMessage: '',
  minLengthForSearchResults: undefined,
  secondary: false,
  style: undefined,
  className: undefined,
  hidePlaceHolderOnFocus: false,
  resultsStyle: undefined,
  resultsClassName: undefined,
  resultStyle: undefined,
  resultClassName: undefined,
  prefix: undefined,
  suffix: undefined,
  currencySuffix: undefined,
  number: undefined,
  validation: {},
  onSelect: undefined,
  label: undefined,
  mask: undefined,
  disabled: false,
  disableAutocomplete: false,
  highlight: false,
  dirtyErrors: [],
  dirtyTouchedErrors: [],
  submitErrors: [],
};

export default TextInput;
